import { useId, useRef, useState, type FormEvent } from 'react'

import { useAssistant } from '../../contexts/AssistantContext'
import { COUNTER_FROM, MAX_MESSAGE_CODE_POINTS, isSendable, messageLength } from '../../lib/chatText'
import { retryDeadline } from '../../lib/assistantThread'
import { useSecondsUntil } from './useRetryClock'

/**
 * Where a message is written (agent-1-web W2).
 *
 * The counter counts the server's way (`chatText.ts`, C70), not
 * `string.length`, and there is no `maxLength` for the same reason: the
 * browser's limit is UTF-16 units, so an emoji would cost two of "1,000
 * characters" here and one at the server.
 *
 * Sending is off while a turn is in flight, while the assistant is absent, and
 * until a 429/503's `Retry-After` has passed; Enter sends, because the field
 * is the form's only input.
 */
export function Composer({ placeholder }: { placeholder: string }) {
  const assistant = useAssistant()
  const inputId = useId()
  const countId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')

  // A refused message's words come back (adjusted during render, not in an effect).
  const draftBack = assistant?.draftBack ?? null
  // A draft restored across a sign-in (agent-1.1 S9.1) is applied by the first
  // composer that mounts; any other is applied only when it changes, so reopening
  // the drawer never brings back words that were already sent.
  const [lastDraft, setLastDraft] = useState(draftBack?.restored ? null : draftBack)
  if (draftBack !== lastDraft) {
    setLastDraft(draftBack)
    if (draftBack) setText(draftBack.text)
  }

  const notice = assistant?.thread.notice
  const retryAt = assistant?.wait?.retryAt ?? retryDeadline(notice ?? null)
  const waiting = useSecondsUntil(retryAt) !== 0

  if (!assistant) return null
  const busy = assistant.thread.turn !== null
  const blocked = busy || assistant.thread.loading !== null || assistant.absent || waiting
  // agent-1.1 S6: while an administrator's pause lasts, the composer is shut, as
  // when the assistant is absent; the words the person typed stay in it.
  const paused = waiting && (assistant.wait?.code === 'chat.paused' || (notice?.kind === 'refused' && notice.code === 'chat.paused'))
  const length = messageLength(text)
  const sendable = isSendable(text)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!assistant || blocked || !sendable) return
    assistant.send(text)
    setText('')
  }

  return (
    <form className="assistant-composer" onSubmit={submit}>
      <label className="visually-hidden" htmlFor={inputId}>
        Ask the assistant
      </label>
      <input
        ref={inputRef}
        id={inputId}
        className="assistant-composer__input"
        type="text"
        autoComplete="off"
        enterKeyHint="send"
        placeholder={placeholder}
        value={text}
        disabled={assistant.absent || paused}
        aria-describedby={length >= COUNTER_FROM ? countId : undefined}
        onChange={(e) => setText(e.target.value)}
      />
      {length >= COUNTER_FROM && (
        <span
          id={countId}
          className={`assistant-composer__count${length > MAX_MESSAGE_CODE_POINTS ? ' assistant-composer__count--over' : ''}`}
        >
          {length.toLocaleString('en-GB')} / {MAX_MESSAGE_CODE_POINTS.toLocaleString('en-GB')}
        </span>
      )}
      <button type="submit" className="assistant-composer__send" disabled={blocked || !sendable} aria-label="Send">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </form>
  )
}
